import React, { useState, useRef } from 'react';
import { Modal } from '@common/Modal';
import { Button } from '@common/Button';
import { Upload, FileText, Image, X, AlertTriangle, Check, Info } from 'lucide-react';
import { importData } from '@utils/import';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';

export const ImportModal = ({ isOpen, onClose, onImport, surveyId }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setError('');
    setImportResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;
    
    setFile(droppedFile);
    setError('');
    setImportResult(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Check file extension
      const extension = file.name.split('.').pop().toLowerCase();
      const supportedFormats = ['geojson', 'json', 'kml', 'kmz', 'gpx', 'zip'];
      
      if (!supportedFormats.includes(extension)) {
        throw new Error(`Unsupported file format: ${extension}. Supported formats are: ${supportedFormats.join(', ')}`);
      }
      
      // Import data
      const result = await importData(file);
      
      // Ensure result has the expected structure with default values
      const normalizedResult = {
        points: result?.points || [],
        routes: result?.routes || [],
        layers: result?.layers || [],
        images: result?.images || []
      };
      
      setImportResult(normalizedResult);
      logInfo(`Import successful: ${normalizedResult.points.length} POIs, ${normalizedResult.routes.length} routes`);
      
      // Call onImport callback with normalized result
      if (onImport) {
        await onImport(normalizedResult, surveyId);
      }
      
      setToast({
        type: 'success',
        message: `Import successful: ${normalizedResult.points.length} POIs, ${normalizedResult.routes.length} routes`
      });
      
      // Close modal after successful import
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      logError('Import failed:', error);
      setError(error.message || 'Failed to import file');
      setToast({
        type: 'error',
        message: error.message || 'Failed to import file'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setError('');
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Import Data">
      <div className="p-6 space-y-6">
        {/* File Drop Zone */}
        <div 
          className={`border-2 border-dashed rounded-lg p-8 text-center ${
            error ? 'border-red-500' : 'border-gray-600 hover:border-gray-500'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept=".geojson,.json,.kml,.kmz,.gpx,.zip"
          />
          
          {file ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2">
                {file.name.endsWith('.geojson') || file.name.endsWith('.json') ? (
                  <FileText className="w-12 h-12 text-blue-400" />
                ) : file.name.endsWith('.zip') ? (
                  <div className="flex">
                    <FileText className="w-12 h-12 text-blue-400" />
                    <Image className="w-8 h-8 text-green-400 -ml-4 mt-4" />
                  </div>
                ) : (
                  <FileText className="w-12 h-12 text-blue-400" />
                )}
              </div>
              <div>
                <p className="text-white font-medium">{file.name}</p>
                <p className="text-sm text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                onClick={resetForm}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded-lg inline-flex items-center gap-1"
              >
                <X className="w-4 h-4" />
                <span>Remove</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="w-12 h-12 text-gray-400 mx-auto" />
              <p className="text-gray-300">Drag and drop your file here or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Browse Files
              </button>
              <p className="text-sm text-gray-400">
                Supported formats: GeoJSON, KML, KMZ, GPX, ZIP
              </p>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className="bg-green-500/10 text-green-400 p-4 rounded-lg flex items-start gap-2">
            <Check className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Import Successful</p>
              <ul className="list-disc list-inside mt-1">
                <li>{importResult.points.length} POIs imported</li>
                <li>{importResult.routes.length} routes imported</li>
                <li>{importResult.layers.length} layers found</li>
                <li>{importResult.images.length} images processed</li>
              </ul>
            </div>
          </div>
        )}

        {/* Information */}
        <div className="bg-gray-700/30 p-4 rounded-lg flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-gray-300">
            <p className="mb-1">
              Import data from various formats to create routes and POIs.
            </p>
            <p>
              For best results with images, use a ZIP file containing both GeoJSON and images.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="default"
            className="bg-gray-700 hover:bg-gray-600 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={!file || loading}
            loading={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};